import { useState, useEffect, useSyncExternalStore, useCallback, useMemo } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import type { CommandLogEntry } from '../commands/commandLog';

function formatRelativeTime(timestamp: number, now: number): string {
  const diff = Math.max(0, now - timestamp);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

function formatAbsoluteTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

const ACTOR_COLORS: Record<string, string> = {
  user: '#3b82f6',
  agent: '#8b5cf6',
  multiplayer: '#22c55e',
};

function ActorDot({ type }: { type: string }) {
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: ACTOR_COLORS[type] ?? '#94a3b8',
        flexShrink: 0,
      }}
      title={type}
    />
  );
}

/** A single command or a group of commands in a transaction */
type ListItem =
  | { kind: 'single'; entry: CommandLogEntry; index: number }
  | { kind: 'transaction'; txId: string; label: string; entries: { entry: CommandLogEntry; index: number }[]; timestamp: number };

function groupEntries(entries: readonly CommandLogEntry[]): ListItem[] {
  const items: ListItem[] = [];
  const txMap = new Map<string, ListItem & { kind: 'transaction' }>();

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry.transactionId) {
      let group = txMap.get(entry.transactionId);
      if (!group) {
        group = {
          kind: 'transaction',
          txId: entry.transactionId,
          label: entry.transactionLabel ?? 'transaction',
          entries: [],
          timestamp: entry.timestamp,
        };
        txMap.set(entry.transactionId, group);
        items.push(group);
      }
      group.entries.push({ entry, index: i });
    } else {
      items.push({ kind: 'single', entry, index: i });
    }
  }
  return items;
}

function CommandRow({
  entry,
  now,
  indent,
  onClick,
}: {
  entry: CommandLogEntry;
  now: number;
  indent: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 24,
        width: '100%',
        padding: indent ? '0 8px 0 20px' : '0 8px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 10,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ color: '#94a3b8', width: 24, flexShrink: 0, textAlign: 'right' }}>
        {formatRelativeTime(entry.timestamp, now)}
      </span>
      <ActorDot type={entry.actor.type} />
      <span
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          color: '#374151',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}
      >
        {entry.command.type}
      </span>
      {entry.coalescedCount > 1 && (
        <span style={{ color: '#94a3b8', flexShrink: 0, fontSize: 9 }}>
          {entry.coalescedCount}x
        </span>
      )}
    </button>
  );
}

function TransactionSpan({
  item,
  now,
  onSelect,
}: {
  item: ListItem & { kind: 'transaction' };
  now: number;
  onSelect: (index: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const count = item.entries.length;

  return (
    <div>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: 24,
          width: '100%',
          padding: '0 8px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 10,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ color: '#94a3b8', width: 24, flexShrink: 0, textAlign: 'right' }}>
          {formatRelativeTime(item.timestamp, now)}
        </span>
        {expanded
          ? <ChevronDown size={10} style={{ color: '#94a3b8', flexShrink: 0 }} />
          : <ChevronRight size={10} style={{ color: '#94a3b8', flexShrink: 0 }} />
        }
        <span
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            color: '#6366f1',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {item.label}
        </span>
        <span style={{ color: '#94a3b8', flexShrink: 0 }}>
          {count}x
        </span>
      </button>
      {expanded && (
        <div style={{ borderLeft: '2px solid #e0e7ff', marginLeft: 11 }}>
          {item.entries.map(({ entry, index }) => (
            <CommandRow
              key={index}
              entry={entry}
              now={now}
              indent
              onClick={() => onSelect(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CommandLogListView({
  entries,
  now,
  onSelect,
}: {
  entries: readonly CommandLogEntry[];
  now: number;
  onSelect: (index: number) => void;
}) {
  const items = useMemo(() => groupEntries(entries).reverse(), [entries]);

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {entries.length === 0 && (
        <div style={{ padding: 12, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
          No commands yet
        </div>
      )}
      {items.map(item =>
        item.kind === 'transaction' ? (
          <TransactionSpan key={`tx-${item.txId}`} item={item} now={now} onSelect={onSelect} />
        ) : (
          <CommandRow
            key={`cmd-${item.index}`}
            entry={item.entry}
            now={now}
            indent={false}
            onClick={() => onSelect(item.index)}
          />
        ),
      )}
    </div>
  );
}

function CommandLogDetailView({
  entry,
  now,
  onBack,
}: {
  entry: CommandLogEntry;
  now: number;
  onBack: () => void;
}) {
  const payload = JSON.stringify(entry.command, null, 2);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: 28,
          padding: '0 8px',
          border: 'none',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          cursor: 'pointer',
          fontSize: 10,
          color: '#374151',
          fontWeight: 500,
          flexShrink: 0,
          width: '100%',
          textAlign: 'left',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; }}
      >
        <ArrowLeft size={10} />
        Back
      </button>

      <div style={{ padding: 8, fontSize: 10, overflowY: 'auto', flex: 1 }}>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, fontSize: 11, marginBottom: 8, color: '#1e293b' }}>
          {entry.command.type}
        </div>

        <div style={{ color: '#64748b', marginBottom: 4 }}>
          <span style={{ fontWeight: 500 }}>Time: </span>
          {formatAbsoluteTime(entry.timestamp)} ({formatRelativeTime(entry.timestamp, now)} ago)
        </div>

        <div style={{ color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontWeight: 500 }}>Actor: </span>
          <ActorDot type={entry.actor.type} />
          {entry.actor.type}{entry.actor.name ? ` (${entry.actor.name})` : ''}
        </div>

        {entry.transactionId && (
          <div style={{ color: '#64748b', marginBottom: 4 }}>
            <span style={{ fontWeight: 500 }}>Transaction: </span>
            <span style={{ color: '#6366f1' }}>{entry.transactionLabel ?? entry.transactionId}</span>
          </div>
        )}

        <pre
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 10,
            lineHeight: 1.5,
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 4,
            padding: 8,
            margin: '8px 0 0',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: '#334155',
          }}
        >
          {payload}
        </pre>
      </div>
    </div>
  );
}

export function CommandHistoryPanel() {
  const { txManager } = useCanvasStores();
  const commandLog = txManager.commandLog;

  const entries = useSyncExternalStore(
    useCallback((cb: () => void) => commandLog.subscribe(cb), [commandLog]),
    () => commandLog.getEntries(),
  );

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  if (selectedIndex !== null && selectedIndex < entries.length) {
    return (
      <CommandLogDetailView
        entry={entries[selectedIndex]}
        now={now}
        onBack={() => setSelectedIndex(null)}
      />
    );
  }

  return (
    <CommandLogListView
      entries={entries}
      now={now}
      onSelect={setSelectedIndex}
    />
  );
}
